import { useCallback } from "react";
import { resolveEnvironment, ServiceName } from "@riot-private/riotpay-sdk-common";
import {
  completeRedeemPrepaidCode,
  startRedeemPrepaidCode,
  startSession,
  RedeemPrepaidCodeOutHttp,
  StartPrepaidCheckoutOutHttp,
} from "@riot-private/riotpay-sdk-pmc";
import { resolveLocation } from "@riot-private/riotpay-sdk-location";
import siteConfig from "@/variables";
import { useSessionStorage } from "react-use";
import { BuyerInfoData } from "../components/BuyerInfoDetails";
import { toRiotLocale } from "@/utils/rioti18n";

// Initial state objects for type definitions
export const INITIAL_START_PREPAID_CHECKOUT_OUT_HTTP: StartPrepaidCheckoutOutHttp = {
  success: false,
};

export const INITIAL_REDEEM_PREPAID_CODE_OUT_HTTP: RedeemPrepaidCodeOutHttp = {
  success: false,
  failureCode: "",
  invalidReasonCode: "",
  failureReason: "",
  playerFacingId: "",
  newBalance: 0,
  purchaseId: "",
  purchasedPricePoint: {
    realAmountCents: 0,
    realCurrencyCode: "",
    virtualAmount: 0,
    virtualBonusAmount: 0,
    virtualCurrencyCode: "",
  },
  virtualContent: [],
};

export function useCashCode() {
  const environment = siteConfig.BUILD_ENV;

  const [code, setCode] = useSessionStorage<string>(siteConfig.KEY_REDEEM_CODE, "", true);

  // RiotPay SDK state
  const [riotpayInstance, setRiotpayInstance] = useSessionStorage<string | null>(
    siteConfig.KEY_REDEEM_RIOTPAY_INSTANCE,
    null,
  );
  const [sessionToken, setSessionToken] = useSessionStorage<string | null>(siteConfig.KEY_REDEEM_SESSION_TOKEN, null);

  // Validation and Redemption responses
  const [validationDetails, setValidationDetails] = useSessionStorage<StartPrepaidCheckoutOutHttp>(
    siteConfig.KEY_REDEEM_CASH_CODE_VALIDATION_DETAILS,
    INITIAL_START_PREPAID_CHECKOUT_OUT_HTTP,
  );
  const [redemptionDetails, setRedemptionDetails] = useSessionStorage<RedeemPrepaidCodeOutHttp>(
    siteConfig.KEY_REDEEM_CASH_CODE_REDEMPTION_DETAILS,
    INITIAL_REDEEM_PREPAID_CODE_OUT_HTTP,
  );

  const validateCode = useCallback(
    async (
      game: string,
      rsoToken: string,
      language: string,
      country?: string,
      gameShard?: string,
      buyerInfo?: BuyerInfoData,
    ) => {
      if (!code) {
        throw new Error("Prepaid code is required");
      }

      // Step 1: Resolve location
      const env = resolveEnvironment(environment, ServiceName.LOCATION);
      const locationResult = await resolveLocation(
        env, // environment
        rsoToken, // rsoAccessToken
        game, // store
        "shop", // platform
        country || "", // country
        undefined, // patchline (optional)
        gameShard, // gameShard (optional)
      );
      const { riotpayInstance: instance, storefrontAccountCode } = locationResult;
      setRiotpayInstance(instance);

      // Step 2: Initialize session
      const locale = toRiotLocale(language);
      const sessionResult = await startSession(environment, instance, rsoToken, {
        storefrontAccountCode,
        localeId: locale,
      });
      const token = sessionResult.token;
      setSessionToken(token);

      // Step 3: Validate the code
      const checkoutResult = await startRedeemPrepaidCode(environment, instance, token, {
        prepaidCode: code,
        ...buyerInfo,
      });
      setValidationDetails(checkoutResult);

      return checkoutResult;
    },
    [code, environment, setRiotpayInstance, setSessionToken, setValidationDetails],
  );

  const redeemCode = useCallback(async () => {
    if (!sessionToken) {
      throw new Error("Session token is required");
    }
    if (!riotpayInstance) {
      throw new Error("RiotPay instance is required");
    }
    if (!code) {
      throw new Error("Code is required");
    }
    if (!validationDetails?.purchaseId) {
      throw new Error("Purchase ID is required");
    }

    const result = await completeRedeemPrepaidCode(environment, riotpayInstance, sessionToken, {
      prepaidCode: code,
      purchaseId: validationDetails.purchaseId,
    });

    setRedemptionDetails(result);
    return result;
  }, [riotpayInstance, sessionToken, code, validationDetails, environment, setRedemptionDetails]);

  const resetCode = useCallback(() => {
    setCode("");
    setValidationDetails(INITIAL_START_PREPAID_CHECKOUT_OUT_HTTP);
    setRedemptionDetails(INITIAL_REDEEM_PREPAID_CODE_OUT_HTTP);
    setRiotpayInstance(null);
    setSessionToken(null);
  }, [setCode, setValidationDetails, setRedemptionDetails, setRiotpayInstance, setSessionToken]);

  return {
    code,
    setCode,
    validationDetails,
    redemptionDetails,
    validateCode,
    redeemCode,
    resetCode,
  };
}
